import { useState, useEffect } from 'react';
import { SOCKET_EVENTS } from '../constants/socketEvents';
import { useNavigate } from 'react-router-dom';
import peerConsultationService from '../services/peerConsultationService';
import UnifiedTeleconsultationService from '../services/UnifiedTeleconsultationService';
import { logger } from '../utils/logger';
import socketService from '../services/unifiedSocketService';
import { z } from 'zod';

export interface IncomingCall {
  consultationId: string | number;
  requesterName: string;
  notes?: string;
  isPatientCall?: boolean;
}

// Schémas de validation temporaires pour le hook
const PeerCallSchema = z.object({
  sessionId: z.union([z.string(), z.number()]).optional(),
  consultationId: z.union([z.string(), z.number()]).optional(),
  requesterName: z.string().optional(),
  requesterId: z.union([z.string(), z.number()]).optional(),
  notes: z.string().optional(),
  callerName: z.string().optional(),
  callerId: z.union([z.string(), z.number()]).optional(),
  agoraRtcToken: z.string().optional(),
  doctorRtcToken: z.string().optional(),
  roomId: z.string().optional(),
});

export const usePeerConsultationNotifications = () => {
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);

  useEffect(() => {
    const handleIncomingPeerCall = (data: z.infer<typeof PeerCallSchema>) => {
      logger.debug('Received peer consultation start via service:', data);
      const sessionId = data.sessionId ?? data.consultationId;
      if (!sessionId) {
        logger.warn('Invalid peer consultation start data:', data);
        return;
      }
      setIncomingCall({
        consultationId: sessionId,
        requesterName: data.requesterName || `Medecin #${data.requesterId ?? '-'}`,
        notes: data.notes,
        isPatientCall: false,
      });
    };

    const handleIncomingPatientCall = (data: z.infer<typeof PeerCallSchema>) => {
      logger.debug('Received patient incoming call:', data);
      const sessionId = data.consultationId;
      
      if (!sessionId) {
          logger.warn('Received patient incoming call with invalid consultationId:', data);
          return;
      }
  
      setIncomingCall({
        consultationId: sessionId,
        requesterName: data.callerName || `Patient #${data.callerId ?? '-'}`,
        isPatientCall: true,
      });
    };

    const handleHandshake = (data: z.infer<typeof PeerCallSchema>) => {
      logger.debug('Received consultation handshake globally:', data);
      const sessionId = data.sessionId ?? data.consultationId;
      if (incomingCall && String(incomingCall.consultationId) === String(sessionId)) {
        setIncomingCall(null);
        navigate(`/doctor/teleconsultation/${sessionId}`, {
          state: {
            rtcToken: data.agoraRtcToken || data.doctorRtcToken,
            channelName: data.roomId,
          },
        });
      }
    };

    socketService.validatedOn(SOCKET_EVENTS.PEER_CONSULTATION_REQUESTED, PeerCallSchema, handleIncomingPeerCall);
    socketService.validatedOn(SOCKET_EVENTS.PEER_CONSULTATION_STARTED, PeerCallSchema, handleIncomingPeerCall);
    socketService.validatedOn(SOCKET_EVENTS.CALL_INCOMING, PeerCallSchema, handleIncomingPatientCall);
    socketService.validatedOn(SOCKET_EVENTS.CONSULTATION_HANDSHAKE, PeerCallSchema, handleHandshake);

    return () => {
        socketService.off(SOCKET_EVENTS.PEER_CONSULTATION_REQUESTED);
        socketService.off(SOCKET_EVENTS.PEER_CONSULTATION_STARTED);
        socketService.off(SOCKET_EVENTS.CALL_INCOMING);
        socketService.off(SOCKET_EVENTS.CONSULTATION_HANDSHAKE);
    };
  }, [incomingCall, navigate]);

  const acceptCall = async () => {
    if (!incomingCall) return;
    try {
      if (incomingCall.isPatientCall) {
        // Appeler API pour accepter le patient (provoque l'émission de CONSULTATION_HANDSHAKE)
        await UnifiedTeleconsultationService.acceptNextPatient(incomingCall.consultationId);
      } else {
        setIncomingCall(null);
        navigate(`/doctor/teleconsultation/${incomingCall.consultationId}`);
      }
    } catch (err) {
      logger.error('Error accepting call:', err);
    }
  };

  const declineCall = async () => {
    if (!incomingCall) return;
    try {
      if (incomingCall.isPatientCall) {
        // Ignorer l'appel, il reste dans la salle d'attente
        // Ou bien, on peut appeler updateStatus pour CANCELLED (mais un autre doc peut le prendre !)
        // On va juste fermer la modale
        setIncomingCall(null);
      } else {
        await peerConsultationService.updateStatus(incomingCall.consultationId, 'CANCELLED');
        setIncomingCall(null);
      }
    } catch (err) {
      logger.error('Failed to decline call:', err);
      // Fallback
      setIncomingCall(null);
    }
  };

  return { incomingCall, acceptCall, declineCall };
};
